<template>
  <v-card class="type-selection-card rounded-lg" outlined>
    <v-card-title class="section-title">Type selection</v-card-title>
    <v-divider class="section-divider" />
    <v-card-text class="section-body">
      <v-form>
        <v-tabs v-model="selectedTabModel" class="type-tabs mb-3">
          <v-tab v-for="tab in tabs" :key="tab.value" class="type-tab" :value="tab.value">
            {{ tab.label }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="selectedTabModel">
          <v-tab-item :value="0">
            <v-list class="type-list" outlined nav dense>
              <v-list-item-group v-model="printTypeIndex" color="primary" mandatory>
                <v-list-item
                  v-for="(type, index) in printTypes"
                  :key="type.value"
                  :value="index"
                  class="type-list-item"
                >
                  <v-list-item-content>
                    <v-list-item-title class="type-list-item-title">{{ type.label }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-tab-item>
          <v-tab-item :value="1">
            <v-textarea outlined :value="prompt" rows="8" @input="$emit('update:prompt', $event)" />
            <v-list class="type-list mt-2" outlined nav dense>
              <v-list-item-group v-model="modelSync" color="primary">
                <v-list-item
                  v-for="m in models"
                  :key="m.value"
                  :value="m.value"
                  class="type-list-item"
                >
                  <v-list-item-content>
                    <v-list-item-title class="type-list-item-title">{{ m.label }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-tab-item>
        </v-tabs-items>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import prompts from '@/assets/mizodia/custom-print-prompts.json'
import { TABS, PRINT_TYPES, MODELS, defaultModelValueForPrintType } from './constants'

export default {
  name: 'TypeSelectionCard',
  props: {
    selectedTab: { type: Number, default: 0 },
    printType: { type: String, default: 'fingerprint' },
    prompt: { type: String, default: '' },
    model: { type: String, default: 'gemini-2.5-flash-image' },
    tabs: { type: Array, default: () => TABS },
    printTypes: { type: Array, default: () => PRINT_TYPES },
    models: { type: Array, default: () => MODELS },
  },
  computed: {
    effectivePrintTypeKey() {
      return typeof this.printType === 'number'
        ? this.printTypes[this.printType]?.value || 'fingerprint'
        : this.printType || 'fingerprint'
    },
    selectedTabModel: {
      get() {
        return this.selectedTab
      },
      set(val) {
        const prev = this.selectedTab
        this.$emit('update:selectedTab', val)
        if (val === 1 && prev === 0) {
          this.$emit('update:prompt', prompts[this.effectivePrintTypeKey] || '')
        }
      },
    },
    /** v-list-item-group uses index; sync with parent's printType (string). */
    printTypeIndex: {
      get() {
        const idx = this.printTypes.findIndex((t) => t.value === this.printType)
        return idx >= 0 ? idx : 0
      },
      set(index) {
        const value = this.printTypes[Number(index)]?.value ?? 'fingerprint'
        this.$emit('update:printType', value)
        this.$emit('update:selectedTab', 0)
        this.$emit('update:prompt', prompts[value] || '')
        this.$emit('update:model', defaultModelValueForPrintType(value))
      },
    },
    /** v-list-item-group emits `change` (Vuetify Proxyable), not `input` — sync via v-model. */
    modelSync: {
      get() {
        return this.model
      },
      set(val) {
        if (val != null) this.$emit('update:model', val)
      },
    },
  },
}
</script>

<style scoped>
.type-selection-card {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  height: 100%;
  min-height: 280px;
  text-align: left;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.section-title {
  font-size: 0.9375rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: left;
  padding: 18px 20px 14px !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

.section-divider {
  margin: 0 20px !important;
  border-color: rgba(255, 255, 255, 0.06) !important;
}

.section-body {
  text-align: left;
  padding: 18px 20px 20px !important;
  padding-top: 16px !important;
}

.type-tabs {
  margin-bottom: 16px !important;
}

.type-tabs >>> .v-tabs-bar {
  background: transparent !important;
}

.type-tabs >>> .v-tab {
  text-transform: none !important;
  font-size: 0.8125rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
}

.type-tabs >>> .v-tab--active {
  color: #64b5f6 !important;
}

.type-list {
  background: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 10px;
  overflow: hidden;
}

.type-list .type-list-item {
  min-height: 44px;
  padding-left: 12px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.15s ease;
}

.type-list .type-list-item:last-child {
  border-bottom: none;
}

.type-list .type-list-item:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.type-list .type-list-item.v-list-item--active {
  background: rgba(66, 165, 245, 0.15) !important;
  border-left: 3px solid #42a5f5;
  padding-left: 9px !important;
}

.type-list-item-title {
  font-size: 0.8125rem !important;
  font-weight: 500 !important;
  color: rgba(255, 255, 255, 0.88) !important;
}
</style>
